import type React from "react";
import type { SetStateAction } from "react";
import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { useFeatureToggleContext } from "contexts/FeatureToggleContext";
import { useVideoCallContext } from "contexts/VideoCallContext";
import { useVoiceCallContext } from "contexts/VoiceCallContext";
import { Avatar } from "shared/atoms/Avatar";
import { Hidden } from "shared/atoms/Hidden";
import { IconButton } from "shared/atoms/IconButton";
import { MeatballMenu } from "shared/atoms/MeatballMenu";
import { Notification } from "shared/atoms/Notification";
import { PremiumIcon } from "shared/atoms/PremiumIcon";
import { CurrentPatientContext } from "utils/contexts";
import { Feature } from "utils/features/types";

import {
  AnimatedDropdownIcon,
  AvatarContainer,
  CallOngoingContainer,
  Container,
  MobileContainer,
  MobileContent,
  MobileName,
  Priority,
  isPatientVisible,
} from "./helpers";
import { PatientDataList } from "./PatientDataList";
import { PatientHeaderBanner } from "./PatientHeaderBanner";
import { PatientHeaderDropdownInfo } from "./PatientHeaderDropdownInfo";
import { PatientHeaderDropdownMenu } from "./PatientHeaderDropdownMenu";
import { PatientStickyNote } from "./PatientStickyNote/PatientStickyNote";

interface Props {
  setShowEndTreatmentForm?: React.Dispatch<SetStateAction<boolean>> | undefined;
  showEndTreatmentForm?: boolean | undefined;
}

export const PatientHeaderInfo: React.FC<Props> = ({ setShowEndTreatmentForm, showEndTreatmentForm }) => {
  const { patient, patientError } = useContext(CurrentPatientContext);
  const { hasFeature } = useFeatureToggleContext();
  const { callData } = useVideoCallContext();
  const { voiceCallData } = useVoiceCallContext();
  const { t } = useTranslation();
  const [dropdownState, setDropdownState] = useState({ menuOpen: false, infoOpen: false });
  const ongoingVideoCall = callData ? callData.callee.id === patient.id : false;
  const ongoingVoiceCall = voiceCallData ? voiceCallData?.callee.id === patient.id : false;
  const ongoingCall = ongoingVideoCall || ongoingVoiceCall;

  const toggleEndTreatment = () => {
    if (setShowEndTreatmentForm) {
      setShowEndTreatmentForm(!showEndTreatmentForm);
    }
  };

  const patientName = patient ? `${patient.first_name} ${patient.last_name}` : "";

  return (
    <>
      <Hidden type="aboveTablet">
        <MobileContainer data-testid="patient-header-info-mobile" $offsetTop={0}>
          <MobileContent>
            {patientError ? (
              <Notification variant="danger" style={{ margin: "10px" }}>
                {t("errors.generic")}
              </Notification>
            ) : (
              <>
                <AvatarContainer>
                  <Avatar size={30} fontSize={15} id={patient.id} name={patientName} src={patient.avatars?.medium} />
                  <Priority $hidden={!patient.prio} />
                </AvatarContainer>
                <PremiumIcon premiumType={patient.premium_type} size="small" />
                <MobileName>{patientName}</MobileName>

                <IconButton
                  label=""
                  onClick={() => setDropdownState({ menuOpen: false, infoOpen: !dropdownState.infoOpen })}
                  className="chevron-button"
                >
                  <AnimatedDropdownIcon $open={dropdownState.infoOpen} data-testid="open-info" />
                </IconButton>
              </>
            )}
          </MobileContent>
          {!patientError && (
            <>
              {isPatientVisible(patient) && (
                <MeatballMenu
                  open={dropdownState.menuOpen}
                  toggleMenu={() => setDropdownState({ menuOpen: !dropdownState.menuOpen, infoOpen: false })}
                />
              )}
              <PatientHeaderDropdownMenu
                isOpen={dropdownState.menuOpen}
                onClose={() => setDropdownState({ menuOpen: false, infoOpen: false })}
                showEndTreatmentForm={showEndTreatmentForm}
                setShowEndTreatmentForm={setShowEndTreatmentForm}
              />
              <PatientHeaderDropdownInfo
                isOpen={dropdownState.infoOpen}
                onClose={() => setDropdownState({ menuOpen: false, infoOpen: false })}
                patientError={patientError}
              />
            </>
          )}
        </MobileContainer>
      </Hidden>

      <Hidden type="belowTablet">
        <Container data-testid="patient-header-info-desktop">
          <PatientHeaderBanner />
          {patientError ? (
            <Notification variant="danger" style={{ margin: "10px" }}>
              {t("errors.generic")}
            </Notification>
          ) : (
            <>
              {ongoingCall && <CallOngoingContainer>{t("videocall.ongoing")}</CallOngoingContainer>}

              <PatientDataList toggleEndTreatment={toggleEndTreatment} />

              {hasFeature(Feature.PATIENT_STICKY_NOTE) && <PatientStickyNote />}
            </>
          )}
        </Container>
      </Hidden>
    </>
  );
};
